// Company service — business logic and orchestration.

import { CompanyDao } from './CompanyDao.ts';
import type { SupabaseClient, InvalidateFn } from '../base.ts';
import type { VendorFamilyResult } from "@shared/types/company.ts";

export class CompanyService {
  readonly dao: CompanyDao;

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    this.dao = new CompanyDao(sb, invalidate);
  }

  /** Expand vendor IDs to include parent and child companies (one level).
   *  Returns expanded IDs for candidate fetching AND a family map for grouping. */
  async expandVendorFamily(vendorIds: number[]): Promise<VendorFamilyResult> {
    if (vendorIds.length === 0) return { expandedIds: [], familyMap: new Map() };

    const [selfRows, childRows] = await Promise.all([
      this.dao.getWithParent(vendorIds),
      this.dao.getChildren(vendorIds),
    ]);

    const familyMap = new Map<number, number[]>();
    const expanded = new Set(vendorIds);

    for (const vid of vendorIds) {
      const family = new Set([vid]);

      // Add parent of this vendor
      const self = selfRows.find(r => r.id === vid);
      if (self?.parent_company_id) {
        family.add(self.parent_company_id);
        expanded.add(self.parent_company_id);
      }

      // Add children of this vendor
      for (const r of childRows) {
        if (r.parent_company_id === vid) {
          family.add(r.id);
          expanded.add(r.id);
        }
      }

      familyMap.set(vid, [...family]);
    }

    return { expandedIds: [...expanded], familyMap };
  }
}
